<template>
  <SphericalView
    v-if="dataLoaded"
    :inspection-config="inspectionConfig"
    :snack-bar-type="type"
    :snack-bar-text="message"
    :developer-mode="developerMode"
    :selected-equipment-id="selectedEquipmentId"
    :assets="assets"
    :checked-layers="checkedLayers"
    :platform-name="inspectionDocument.name"
    :platform-id="inspectionDocument._id"
    :corrosion-data="corrosionData"
    :selected-asset="selectedAsset"
    :defect-layers="defectLayers"
    :asset-layers="assetLayers"
    :selected-polygon="selectedPolygon"
    :markers="markers"
    :image-document="imageDocument"
    :reload-image="reloadImage"
    :neighbouring-images="neighbouringImagesInRange"
    :neighbouring-images-max-range="neighbouringImagesMaxRange"
    :neighbouring-images-range="neighbouringImagesRange"
    :stroke-width="strokeWidth"
    :asset-fill-enabled="assetFillEnabled"
    @onUpdateMarkers="handleUpdateMarkers"
    @onSelectMarker="handleSelectMarker"
    @onUpdateCheckedLayers="handleUpdateCheckedLayers"
    @onUpdateLayers="handleUpdateLayers"
    @onSelectEquipmentId="handleSelectEquipmentId"
    @onClearSelectedPoly="handleClearSelectedPoly"
    @onLoadURLQuery="handleLoadURLQuery"
    @onNeighbouringImagesRangeChange="handleNeighbouringImagesRangeChange"
    @onToggleAssetFillEnabled="handleToggleAssetFillEnabled"
    @onSnackbarDisplay="handleSnackbarDisplay"
    @onSnackbarClose="handleSnackbarClose"
  />
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import { notificationType } from '@/components/widgets';
import SphericalView from './SphericalView.vue';

export default {
  name: 'SphericalContainer',
  components: {
    SphericalView,
  },
  imageId: undefined,
  data() {
    return {
      dataLoaded: false,
      reloadImage: false,
      reloadImageRequestNumber: 0,
    };
  },
  computed: {
    ...mapGetters({
      inspectionConfig: 'config/inspectionConfig',
      assetFillEnabled: 'config/assetFillEnabled',
      strokeWidth: 'config/strokeWidth',
      neighbouringImagesRange: 'config/neighbouringImagesRange',
      developerMode: 'config/developerMode',
      notification: 'spherical/notification',
      error: 'spherical/error',
      assets: 'spherical/assets',
      checkedLayers: 'spherical/checkedLayers',
      selectedEquipmentId: 'spherical/selectedEquipmentId',
      inspectionDocument: 'inspectionDocument/currentInspectionInfo',
      selectedAsset: 'spherical/selectedAsset',
      defectLayers: 'spherical/defectLayers',
      assetLayers: 'spherical/assetLayers',
      corrosionData: 'spherical/corrosionData',
      selectedPolygon: 'spherical/selectedPolygon',
      markers: 'spherical/markers',
      imageDocument: 'inspectionDocument/imageDocument',
      neighbouringImages: 'spherical/neighbouringImages',
      labelTaggingMode: 'config/labelTaggingMode',
      highlightingStyle: 'config/highlightingStyle',
    }),
    neighbouringImagesMaxRange() {
      return Math.ceil(Math.max(...this.neighbouringImages.map((image) => image.spherical.range)));
    },
    neighbouringImagesInRange() {
      return this.neighbouringImages.filter(({ spherical }) => spherical.range <= this.neighbouringImagesRange);
    },
    message() {
      return this.error || this.notification;
    },
    type() {
      if (this.error) {
        return notificationType.error;
      }
      if (this.notification) {
        return notificationType.message;
      }
      return notificationType.none;
    },
  },
  watch: {
    imageDocument() {
      // May need to wait for imageDocument to be loaded, if it was not ready in the created lifecycle method
      if (!this.dataLoaded) {
        this.dataLoaded = !isEmpty(this.assets);
      }
    },
    '$route.query.poly': function queryPolyWatcher(value) {
      this.setSelectedMarker({ markerId: value });
    },
    '$store.state.unit': function unitWatcher() {
      this.updateDefectAndAssetLayers();
    },
    highlightingStyle() {
      this.setMarkers();
    },
  },
  async created() {
    const { image } = this.$route.query;
    await Promise.all([
      this.overrideConfigSettings(image),
      this.loadAssetsAndLayers(image),
      this.getNeighbouringImages(image),
      this.loadImageDocument(image),
    ]);

    this.dataLoaded = !isEmpty(this.imageDocument);
  },
  methods: {
    ...mapActions({
      loadAssetsAndLayers: 'spherical/loadAssetsAndLayers',
      reloadAssets: 'spherical/reloadAssets',
      getNeighbouringImages: 'spherical/getNeighbouringImages',
      setMarkers: 'spherical/setMarkers',
      loadImageDocument: 'inspectionDocument/loadImageDocument',
      setSelectedAssetAndEquipmentId: 'spherical/setSelectedAssetAndEquipmentId',
      setSelectedMarker: 'spherical/setSelectedMarker',
      clearSelectedPoly: 'spherical/clearSelectedPoly',
      updateLayers: 'spherical/updateLayers',
      updateCheckedLayers: 'spherical/updateCheckedLayers',
      updateDefectAndAssetLayers: 'spherical/updateDefectAndAssetLayers',
      overrideConfigSettings: 'config/overrideConfigSettings',
      setAssetFillEnabled: 'config/setAssetFillEnabled',
      setNeighbouringImagesRange: 'config/setNeighbouringImagesRange',
      setNotification: 'spherical/setNotification',
      setError: 'spherical/setError',
      clearError: 'spherical/clearError',
      clearNotification: 'spherical/clearNotification',
    }),
    handleUpdateMarkers() {
      this.setMarkers();
    },
    handleSelectMarker(payload) {
      this.setSelectedMarker(payload);
    },
    handleUpdateCheckedLayers(payload) {
      this.updateCheckedLayers(payload);
    },
    handleUpdateLayers() {
      this.updateLayers();
    },
    handleSelectEquipmentId(id) {
      this.setSelectedAssetAndEquipmentId(id);
    },
    handleClearSelectedPoly() {
      this.clearSelectedPoly();
    },
    async handleLoadURLQuery(options = {}) {
      this.reloadImage = false; // May need to wait until assets are reloaded
      this.reloadImageRequestNumber += 1; // Debounce reloading the image

      const { image } = this.$route.query;
      const actions = [this.loadImageDocument(image)];
      if (options.loadAssets) {
        actions.push(this.reloadAssets(image), this.getNeighbouringImages(image));
      }

      await Promise.all(actions);

      this.reloadImageRequestNumber -= 1;
      if (this.reloadImageRequestNumber === 0) {
        this.reloadImage = true;
      }
    },
    handleNeighbouringImagesRangeChange(value) {
      this.setNeighbouringImagesRange(value);
    },
    handleToggleAssetFillEnabled() {
      this.setAssetFillEnabled(!this.assetFillEnabled);
      this.setMarkers();
    },
    handleSnackbarDisplay(message, type) {
      if (type === notificationType.error) {
        this.setError(message);
      } else {
        this.setNotification(message);
      }
    },
    handleSnackbarClose() {
      this.clearNotification();
      this.clearError();
    },
  },
};
</script>
